import React, { ReactNode } from "react";

interface ButtonProps extends Omit<React.ComponentPropsWithoutRef<"button">, "type"> {
  children?: ReactNode;
  size?: "sm" | "md";
  variant?: "primary" | "outline" | "danger" | "ghost" | "text";
  type?: "button" | "submit" | "reset";
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  loading?: boolean;
  fullWidth?: boolean;
  iconOnly?: boolean;
}

const Button: React.FC<ButtonProps> = ({
  children,
  size = "md",
  variant = "primary",
  type = "button",
  startIcon,
  endIcon,
  onClick,
  className = "",
  disabled = false,
  loading = false,
  fullWidth = false,
  iconOnly = false,
  "aria-label": ariaLabel,
  ...rest
}) => {
  // Validate icon-only buttons have aria-label
  if (iconOnly && !ariaLabel) {
    console.warn(
      "Button: icon-only buttons should have an aria-label for accessibility"
    );
  }

  const isDisabled = disabled || loading;

  // Size Classes
  const sizeClasses = iconOnly
    ? {
        sm: "p-2 text-sm", // Square padding for icons
        md: "p-2.5 text-sm", // Square padding for icons
      }
    : {
        sm: "px-4 py-3 text-sm",
        md: "px-5 py-3.5 text-sm",
      };

  // Variant Classes - Using dark text on yellow brand color. Primary and
  // text use the accent-yellow foundation tokens (globals.css) so the
  // brand action color is the single approved shade per theme, dark
  // mode included, instead of a hardcoded dark: override.
  const variantClasses = {
    primary:
      "bg-[var(--accent-yellow)] text-gray-900 shadow-theme-xs hover:bg-[var(--accent-yellow-hover)] disabled:bg-brand-300",
    outline:
      "bg-white text-gray-700 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-400 dark:ring-gray-700 dark:hover:bg-white/[0.03] dark:hover:text-gray-300",
    danger:
      "bg-red-600 text-white shadow-theme-xs hover:bg-red-700 disabled:bg-red-300 dark:bg-red-600 dark:hover:bg-red-700",
    ghost:
      "bg-transparent text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800",
    text: "bg-transparent text-[var(--accent-yellow)] hover:text-[var(--accent-yellow-hover)] shadow-none",
  };

  // Loading spinner component
  const LoadingSpinner = () => (
    <svg
      className="animate-spin h-4 w-4"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      ></circle>
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      ></path>
    </svg>
  );

  return (
    <button
      type={type}
      className={`inline-flex items-center justify-center font-medium gap-2 rounded-lg transition ${
        fullWidth ? "w-full" : ""
      } ${className} ${sizeClasses[size]} ${variantClasses[variant]} ${
        isDisabled ? "cursor-not-allowed opacity-50" : ""
      }`}
      onClick={onClick}
      disabled={isDisabled}
      aria-label={ariaLabel}
      {...rest}
    >
      {loading && <LoadingSpinner />}
      {!loading && startIcon && (
        <span className="flex items-center">{startIcon}</span>
      )}
      {!loading && children}
      {!loading && endIcon && (
        <span className="flex items-center">{endIcon}</span>
      )}
    </button>
  );
};

export default Button;
